import React from "react";
import { withRouter, Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ManageSearchRoundedIcon from "@mui/icons-material/ManageSearchRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import { brand, radius, shadow } from "../../theme/businessFlowsTheme";
import TypeIcon from "../app/typeIcons";
import { attributeLabel, displayValue, fieldsFor, formatLabel, setting, typeInfo } from "../../utils/viewConfig";
import { attributeRows, connectionSentence } from "./relationshipGroups";
import { sortFormats } from "../diagram/formatRoute";
import { SHOW_MAPS } from "../../features";

const CARD_WIDTH = 340;

// Quick fact sheet shown while hovering an object node.
class HoverComponent extends React.Component {
  state = { hovered: false };

  position() {
    const id = this.props?.nodeDatum?.__rd3t?.id;
    const element = id ? document.getElementById(`${id}`) : null;
    if (!element) return null;
    const rect = element.getBoundingClientRect();
    if (isNaN(rect.x) || isNaN(rect.y)) return null;
    const nodeRight = rect.x + 260 * (this.props.zoom || 1);
    const fitsRight = nodeRight + 16 + CARD_WIDTH < window.innerWidth;
    const left = fitsRight ? nodeRight + 12 : Math.max(8, rect.x - CARD_WIDTH - 12);
    const top = Math.min(Math.max(8, rect.y - 40), window.innerHeight - 220);
    return { left, top };
  }

  render() {
    const { nodeDatum, show, path, onShowDetails, onOpenDiagram, onFindInSearch } = this.props;
    const visible = (show || this.state.hovered) && nodeDatum?.guid;
    const pos = visible ? this.position() : null;
    if (!visible || !pos) return null;

    const config = this.props.viewConfig;
    const info = typeInfo(config, nodeDatum.type);
    // The relationship that put this object on the canvas, said as a sentence
    // with the two objects either side of it.
    const connection = connectionSentence(config, {
      relationship: nodeDatum._relationship,
      direction: nodeDatum._direction,
      here: nodeDatum._from,
      there: nodeDatum.fullName,
    });
    // The configured "hover" fields, else everything the object carries.
    const item = nodeDatum.item || { Type: nodeDatum.type, Description: nodeDatum.description };
    // What this object can be opened as, read from the object itself and from
    // what the server found beside it - never guessed from its type, exactly
    // as a search result decides it (advanceSearch/ResultCard): the diagram it
    // has (or is), else the map of what sits beneath it, else its
    // relationships.
    const hasDiagram = Boolean(nodeDatum.hasDiagram || item._diagramFormat);
    // What that diagram is, so the button can say so. A diagram is offered
    // under its own type - BPMN, SVG, Mermaid, PDF - and only the one whose
    // format nothing recorded reads "Diagram" (product owner, 28 Sep 2026).
    const formats = sortFormats(
      (Array.isArray(nodeDatum.diagramFormats) && nodeDatum.diagramFormats.length
        ? nodeDatum.diagramFormats
        : [item._diagramFormat]
      ).filter(Boolean)
    );
    const diagramName = formats.length ? formatLabel(config, formats[0]) : "Diagram";
    // A diagram whose format nothing recorded keeps the plain wording.
    const openDiagramLabel = formats.length ? `Open ${diagramName}` : "Open diagram";
    // SHOW_MAPS=false: maps are hidden, so this falls through to "Explore".
    const hasMap = SHOW_MAPS && !hasDiagram && Boolean(item.hasAssignment);
    const fields = fieldsFor(config, "hover", item);
    const description = fields.includes("Description") ? displayValue(item.Description) : "";
    const others = fields.filter((key) => key !== "Description");
    const listed = others.slice(0, setting(config, "hover.fieldLimit"));
    const more = others.length - listed.length;

    return (
      <Box
        className="bfx"
        onMouseEnter={() => this.setState({ hovered: true })}
        onMouseLeave={() => this.setState({ hovered: false })}
        sx={{
          position: "fixed",
          left: pos.left,
          top: pos.top,
          width: CARD_WIDTH,
          zIndex: 1300,
          p: 2,
          bgcolor: "#fff",
          borderRadius: radius.card,
          border: `1px solid ${brand.line}`,
          boxShadow: shadow.floating,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {nodeDatum.type && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <TypeIcon name={info.icon} sx={{ fontSize: 14, color: info.colour }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {info.label}
                </Typography>
              </Stack>
            )}
            <Typography sx={{ fontWeight: 700, lineHeight: 1.3, overflowWrap: "anywhere" }}>{nodeDatum.fullName || info.label}</Typography>
            {/* How the reader got here, in the direction the edge actually
                runs. Under a strict taxonomy this is obvious from the two
                types and nobody needs it; where the same two types are joined
                several ways it is the whole answer. */}
            {connection && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, overflowWrap: "anywhere" }}>
                {connection}
              </Typography>
            )}
            {/* And what that one edge holds, where it holds anything. The
                values differ per edge, so they belong with the object the
                reader is pointing at rather than with the relationship. */}
            {attributeRows(nodeDatum._relationshipAttributes).map((row) => (
              <Typography key={row.key} variant="caption" color="text.secondary" sx={{ display: "block", overflowWrap: "anywhere" }}>
                {row.label}: <Box component="span" sx={{ fontWeight: 600 }}>{row.value}</Box>
              </Typography>
            ))}
          </Box>
          {nodeDatum.url && (
            <Tooltip title="Open linked document">
              <IconButton
                size="small"
                component={RouterLink}
                to={`${path}/external-link/${nodeDatum.url}`}
                target="_blank"
                aria-label="Open linked document"
              >
                <LinkRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        {description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 1,
              lineHeight: 1.5,
              display: "-webkit-box",
              WebkitLineClamp: 4,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              overflowWrap: "anywhere",
            }}
          >
            {description}
          </Typography>
        )}

        {listed.length > 0 && (
          <Box component="dl" sx={{ m: 0, mt: 1, display: "grid", gridTemplateColumns: "auto minmax(0, 1fr)", columnGap: 1.25, rowGap: 0.25 }}>
            {listed.map((key) => (
              <React.Fragment key={key}>
                <Typography component="dt" variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {attributeLabel(key)}
                </Typography>
                <Typography
                  component="dd"
                  variant="caption"
                  sx={{ m: 0, overflowWrap: "anywhere", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                >
                  {displayValue(item[key])}
                </Typography>
              </React.Fragment>
            ))}
          </Box>
        )}
        {more > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            {more} more under Details
          </Typography>
        )}

        {/* One row of equal buttons: the same three ways of looking at an
            object that every other view offers, at one size and weight. */}
        <Box
          sx={{
            mt: 1.75,
            display: "grid",
            gap: 0.75,
            gridTemplateColumns: `repeat(${2 + (onFindInSearch ? 1 : 0)}, minmax(0, 1fr))`,
            "& .MuiButton-root": {
              height: 32,
              px: 1,
              borderRadius: radius.control,
              fontSize: "0.78rem",
              fontWeight: 600,
              whiteSpace: "nowrap",
              minWidth: 0,
            },
            "& .MuiButton-startIcon": { mr: 0.5 },
            "& .MuiButton-startIcon > *:nth-of-type(1)": { fontSize: 16 },
          }}
        >
          {hasDiagram || hasMap ? (
            <Tooltip title={hasDiagram ? `${openDiagramLabel} in a new tab` : "Open the map of what sits beneath, in a new tab"}>
              <Button
                size="small"
                variant="contained"
                aria-label={hasDiagram ? openDiagramLabel : "Open map"}
                startIcon={hasDiagram ? <AccountTreeRoundedIcon /> : <GridViewRoundedIcon />}
                onClick={() => onOpenDiagram(nodeDatum, formats[0])}
              >
                {hasDiagram ? diagramName : "Map"}
              </Button>
            </Tooltip>
          ) : (
            <Tooltip title="Explore this object's relationships in a new tab">
              <Button
                size="small"
                variant="contained"
                component={RouterLink}
                to={`${path}/object/${nodeDatum.guid}`}
                target="_blank"
                aria-label="Explore relationships"
                startIcon={<HubRoundedIcon />}
              >
                Explore
              </Button>
            </Tooltip>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<InfoOutlinedIcon />}
            onClick={() => onShowDetails(nodeDatum)}
          >
            Details
          </Button>
          {onFindInSearch && (
            <Button size="small" variant="outlined" startIcon={<ManageSearchRoundedIcon />} onClick={() => onFindInSearch(nodeDatum)}>
              Search
            </Button>
          )}
        </Box>
      </Box>
    );
  }
}

export default withRouter(HoverComponent);
